import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import {
  Sparkles, ArrowRight, Check, BookOpenCheck, ClipboardList, GraduationCap,
  Timer, BarChart3, MessageSquareHeart, Trophy, Lightbulb, Target,
  School, Users2, GlobeLock, BookOpen, ScanText, Pencil, Crown, Star
} from "lucide-react";

const features = [
  { num: "01", icon: BookOpenCheck, title: "Grammar Improvement", color: "bg-emerald-100 text-emerald-700",
    body: "Targeted drills across subject-verb agreement, comma rules, run-ons, fragments, and more — with instant explanations, not red squiggles." },
  { num: "02", icon: ClipboardList, title: "Essay Coaching", color: "bg-indigo-100 text-indigo-700",
    body: "Plan thesis statements, structure body paragraphs, and address counterarguments. The coach guides — students write." },
  { num: "03", icon: GraduationCap, title: "SAT/AP Writing Prep", color: "bg-amber-100 text-amber-700",
    body: "Time-pressured essays scored against the real SAT/AP rubric (Reading · Analysis · Writing). Strategy, evidence, and rhetorical moves." },
  { num: "04", icon: Timer, title: "Interactive Writing Practice", color: "bg-rose-100 text-rose-700",
    body: "Daily prompts, timed sprints, vocabulary challenges, and revision drills that turn writing into deliberate practice." },
  { num: "05", icon: BarChart3, title: "Progress Tracking", color: "bg-sky-100 text-sky-700",
    body: "Watch rubric scores climb week over week. See where you're strong, where you're stuck, and what to practice next." },
  { num: "06", icon: MessageSquareHeart, title: "Personalized Feedback", color: "bg-violet-100 text-violet-700",
    body: "AI feedback tuned to your grade level — warm and encouraging for younger writers, rigorous and analytical for AP students." },
  { num: "07", icon: Trophy, title: "Student Success Benefits", color: "bg-orange-100 text-orange-700",
    body: "Streaks, badges, and visible growth that turn writing into a habit — because confident writers practice more, and practice builds confident writers." },
];

const audience = [
  { icon: Pencil, label: "Middle School", desc: "Foundational structure, clarity, and confidence" },
  { icon: BookOpen, label: "High School", desc: "Analytical writing & literary analysis" },
  { icon: GraduationCap, label: "AP Students", desc: "Advanced argumentation & rhetorical strategy" },
  { icon: Target, label: "SAT Prep", desc: "Timed essays scored on the real rubric" },
  { icon: GlobeLock, label: "ESL Learners", desc: "Grammar with explanation, not just correction" },
  { icon: Users2, label: "Parents", desc: "Tips, talking points, and progress visibility" },
  { icon: School, label: "Teachers", desc: "Rubric-aligned coaching that scales 1:1 attention" },
];

const exercises = [
  { icon: Pencil, t: "Writing Exercises", d: "Open-ended prompts that build voice." },
  { icon: BookOpenCheck, t: "Grammar Drills", d: "One sentence, one error, one explanation." },
  { icon: ScanText, t: "Revision Challenges", d: "Improve a draft — don't replace it." },
  { icon: Lightbulb, t: "Vocabulary Building", d: "Word of the day with usage practice." },
  { icon: Timer, t: "Timed Writing", d: "Practice writing under pressure." },
  { icon: ClipboardList, t: "Essay Feedback", d: "Plan, draft, and revise with a coach." },
  { icon: BarChart3, t: "Score Tracking", d: "Rubric scores trend over weeks." },
  { icon: Trophy, t: "Badges & Streaks", d: "Habits that compound." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="WriterSpark | Interactive Writing Improvement Platform for Students"
        description="WriterSpark helps students improve grammar, essay writing, SAT/AP writing skills, and academic communication through interactive practice and personalized feedback."
        path="/"
        keywords="writing improvement, grammar improvement, essay coaching, SAT writing prep, AP writing, analytical writing, ESL writing practice, academic communication, interactive writing platform"
      />
      <PublicHeader />

      {/* Hero */}
      <section className="bg-mesh">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span data-testid="hero-eyebrow" className="inline-flex rounded-full bg-white border-2 border-slate-200 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-indigo-700">
              Interactive Writing Improvement
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight" style={{fontFamily:'Outfit'}}>
              Strengthen <span className="scribble-underline">writing skills</span> through guided practice.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              <strong>WriterSpark</strong> is an interactive writing improvement platform designed to help students strengthen grammar, structure, clarity, analytical thinking, and academic communication skills through guided practice and personalized feedback.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta-primary" className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-7 py-3.5 tactile-shadow flex items-center gap-2">
                Start practicing free <ArrowRight size={18} />
              </Link>
              <Link to="/about" data-testid="hero-cta-secondary" className="bg-white border-2 border-slate-200 text-slate-900 font-bold rounded-full px-7 py-3.5 hover:border-slate-300">
                Learn more
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {["Grade-adapted coaching", "Real rubric feedback", "Track real growth"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check size={16} className="text-emerald-600"/>{t}</span>
              ))}
            </div>
          </div>

          <div className="relative fade-up delay-2">
            <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-amber-300/60 blur-2xl" />
            <div className="absolute -bottom-8 -right-4 w-32 h-32 rounded-full bg-rose-300/60 blur-2xl" />
            <div className="tactile-card overflow-hidden p-0 relative">
              <img
                data-testid="hero-image-ap"
                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="High-school student studying and writing an analytical essay"
                loading="lazy"
                className="w-full h-[360px] sm:h-[440px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is WriterSpark */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12">
          <div>
            <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">What it is</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold leading-tight" style={{fontFamily:'Outfit'}}>
              A writing coach, not a writing crutch.
            </h2>
          </div>
          <div className="space-y-6 text-slate-700 leading-relaxed">
            <p>
              WriterSpark blends the <strong>focus of a tutor</strong> with the <strong>repetition of a Duolingo lesson</strong>. Students plan, draft, revise, and receive feedback inside the platform — building skills, not just submitting assignments.
            </p>
            <p>
              Every interaction is <strong>guided practice</strong>: micro-drills for grammar, daily prompts for fluency, rubric-scored essays for depth, and a Socratic chat coach for stuck moments.
            </p>
          </div>
        </div>
      </section>

      {/* 7 Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">What's inside</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold" style={{fontFamily:'Outfit'}}>
            Seven tools. One growing writer.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.num} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g,'-').replace(/\//g,'-')}`}
                className="tactile-card p-7">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-2xl ${f.color} grid place-items-center`}>
                    <Icon size={22}/>
                  </div>
                  <span className="text-xs uppercase font-bold tracking-widest text-slate-400">{f.num}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily:'Outfit'}}>{f.title}</h3>
                <p className="text-slate-600 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Interactive features grid */}
      <section className="bg-indigo-50/40 border-y border-indigo-100">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">Interactive Practice</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold" style={{fontFamily:'Outfit'}}>Built like an EdTech app, taught like a tutor.</h2>
            <p className="text-slate-600 mt-3 max-w-xl mx-auto">Inspired by what makes Duolingo and Khan Academy work — short, focused, daily reps that compound into real skill.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {exercises.map((e, i) => {
              const Icon = e.icon;
              return (
                <div key={i} data-testid={`exercise-${i}`} className="tactile-card p-5">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center mb-3"><Icon size={18}/></div>
                  <div className="font-bold text-slate-900" style={{fontFamily:'Outfit'}}>{e.t}</div>
                  <div className="text-sm text-slate-600 mt-1">{e.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">Who it's for</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold" style={{fontFamily:'Outfit'}}>Built for every kind of writer.</h2>
        </div>

        {/* Diverse student portrait band */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          {[
            {src:"https://images.unsplash.com/photo-1503676260728-1c00da094a0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", alt:"Black high-school student writing in a notebook"},
            {src:"https://images.unsplash.com/photo-1581726707445-75cbe4efc586?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", alt:"Asian student writing at a desk"},
            {src:"https://images.pexels.com/photos/8500358/pexels-photo-8500358.jpeg?auto=compress&cs=tinysrgb&w=600", alt:"Hispanic middle school student writing in a notebook in a classroom"},
            {src:"https://images.unsplash.com/photo-1571260899304-425eee4c7efc?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", alt:"Middle-school student writing in class"},
          ].map((p,i)=>(
            <div key={i} data-testid={`portrait-${i}`} className="tactile-card overflow-hidden p-0">
              <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-[180px] object-cover" />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {audience.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.label} data-testid={`audience-${a.label.toLowerCase().replace(/\s+/g,'-')}`} className="tactile-card p-5">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center mb-3"><Icon size={18}/></div>
                <div className="font-bold text-slate-900" style={{fontFamily:'Outfit'}}>{a.label}</div>
                <div className="text-sm text-slate-600 mt-1">{a.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why it exists / Differentiation */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <span className="text-xs uppercase font-bold tracking-widest text-amber-400">Why we built it</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold leading-tight" style={{fontFamily:'Outfit'}}>
              Most "AI writing" tools <span className="line-through text-slate-500">help students cheat</span> just write the answer.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              Grammar checkers correct mistakes. Chatbots produce drafts. Both are useful — and neither teaches.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              <strong className="text-white">WriterSpark teaches students how to improve writing skills</strong> instead of only correcting mistakes and writing for them. Every coach response is <em>scaffolded</em>: it asks the right question, models the move, then hands the pen back.
            </p>
          </div>
          <div className="space-y-4">
            {[
              {bad:"Auto-corrects without explaining", good:"Explains the rule + asks you to apply it"},
              {bad:"Writes the essay for you", good:"Coaches your thesis, then your evidence, then your structure"},
              {bad:"One-size feedback", good:"Tone & rigor adapt by grade band"},
              {bad:"Nothing to look back on", good:"Streaks, rubric trends, and a growing badge collection"},
            ].map((row, i) => (
              <div key={i} data-testid={`diff-row-${i}`} className="rounded-2xl bg-slate-800/60 border border-slate-700 p-5 grid grid-cols-2 gap-4">
                <div className="text-sm text-slate-400 line-through">{row.bad}</div>
                <div className="text-sm text-emerald-300 font-semibold">{row.good}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" data-testid="pricing-section" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">Pricing</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold" style={{fontFamily:'Outfit'}}>Start free. Upgrade when you're ready to grow faster.</h2>
          <p className="mt-4 text-slate-600">Every plan teaches — Premium just gives you more reps, deeper feedback, and the full coaching toolkit.</p>
        </div>

        <FounderBanner />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {/* FREE */}
          <div data-testid="plan-free" className="tactile-card p-7 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 grid place-items-center"><Sparkles size={18}/></div>
              <h3 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>Free</h3>
            </div>
            <p className="text-slate-600 text-sm mb-5">Get a real taste of guided writing practice — no credit card.</p>
            <div className="mb-6">
              <span className="text-4xl font-bold" style={{fontFamily:'Outfit'}}>$0</span>
              <span className="text-slate-500 ml-1">/ month</span>
            </div>
            <ul className="space-y-2.5 mb-8 text-sm text-slate-700 flex-1">
              {[
                "5 AI coaching sessions per day",
                "Daily writing prompts & timed sprints",
                "Basic grammar drills with explanations",
                "Vocabulary word of the day",
                "Up to 3 revision dimensions per draft",
                "Streaks, badges & growth paths (Bronze→Gold)",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0"/><span>{f}</span></li>
              ))}
            </ul>
            <Link to="/register" data-testid="plan-free-cta" className="text-center bg-white border-2 border-slate-200 hover:border-indigo-300 text-slate-900 font-bold rounded-full px-6 py-3">
              Start free
            </Link>
          </div>

          {/* PREMIUM — highlighted */}
          <div data-testid="plan-premium" className="tactile-card p-7 flex flex-col relative border-2 border-indigo-600 lg:scale-[1.03] lg:shadow-[0_12px_0_0_rgb(67_56_202)]">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-amber-400 text-slate-900 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
              <Star size={11}/> Most popular
            </span>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center"><Crown size={18}/></div>
              <h3 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>Premium</h3>
            </div>
            <p className="text-slate-600 text-sm mb-5">Full coaching toolkit — built for students who want to actually grow.</p>
            <div className="mb-6">
              <span className="text-4xl font-bold" style={{fontFamily:'Outfit'}}>$9.99</span>
              <span className="text-slate-500 ml-1">/ month</span>
            </div>
            <ul className="space-y-2.5 mb-8 text-sm text-slate-700 flex-1">
              {[
                "Unlimited AI coaching sessions",
                "Advanced Revision Intelligence (up to 6 dimensions)",
                "SAT/AP timed essay scoring on the real rubric",
                "Interactive Writing Conference (Socratic 4-phase coach)",
                "Adaptive grade-banded feedback",
                "Writing growth analytics & rubric trends",
                "Diamond tier on every Growth Path",
                "Vocabulary mastery paths & cross-curricular literacy",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check size={16} className="text-indigo-600 mt-0.5 flex-shrink-0"/><span>{f}</span></li>
              ))}
            </ul>
            <Link to="/register" data-testid="plan-premium-cta" className="text-center bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-6 py-3 tactile-shadow">
              Try Premium
            </Link>
          </div>

          {/* SCHOOL / EDUCATOR */}
          <div data-testid="plan-school" className="tactile-card p-7 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><School size={18}/></div>
              <h3 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>School & Educator</h3>
            </div>
            <p className="text-slate-600 text-sm mb-5">Classroom rosters, teacher dashboards, and per-student progress at scale.</p>
            <div className="mb-6">
              <span className="text-4xl font-bold" style={{fontFamily:'Outfit'}}>Custom</span>
              <span className="text-slate-500 ml-1 text-sm block mt-1">Volume pricing</span>
            </div>
            <ul className="space-y-2.5 mb-8 text-sm text-slate-700 flex-1">
              {[
                "Everything in Premium for every student",
                "Classroom roster with invite codes & email invites",
                "Per-student rubric trends & growth analytics",
                "Aggregate class-level insights",
                "Parent visibility & family roster linking",
                "Rubric-aligned assignment library",
                "Priority support & onboarding",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0"/><span>{f}</span></li>
              ))}
            </ul>
            <Link to="/contact" data-testid="plan-school-cta" className="text-center bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full px-6 py-3">
              Talk to us
            </Link>
          </div>
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">
          Cancel anytime. Premium is billed monthly via Stripe — no surprise fees.
        </p>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="tactile-card p-12 bg-indigo-600 border-indigo-700 text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{fontFamily:'Outfit'}}>Practice writing the way good writers actually got good.</h2>
          <p className="text-indigo-100 max-w-xl mx-auto mb-8">A little, every day, with feedback. WriterSpark is free to start — no credit card.</p>
          <Link to="/register" data-testid="cta-bottom" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-7 py-3.5 tactile-shadow-amber">
            Start your first session <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FounderBanner() {
  const [s, setS] = useState(null);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API}/api/founder/status`).then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return null;
  const pctLeft = Math.round((s.seats_remaining / s.seat_limit) * 100);
  return (
    <div data-testid="founder-banner" className="tactile-card p-6 sm:p-8 mb-10 border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-900">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-amber-400 text-slate-900 grid place-items-center flex-shrink-0">
          <Crown size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50" style={{ fontFamily: "Outfit" }}>Founder Launch Plan</h3>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 text-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
              <Star size={10} /> Limited
            </span>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
            <strong>$6.99/mo</strong> for the first 327 members · <strong>Locked for 12 months</strong> · Founder Badge · Early access to every new feature.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 max-w-xs h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 transition-all" style={{ width: `${100 - pctLeft}%` }} />
            </div>
            <span className="text-xs font-bold text-slate-700 dark:text-slate-300" data-testid="founder-seats-remaining">
              {s.seats_remaining} / {s.seat_limit} seats left
            </span>
          </div>
        </div>
        {s.sold_out ? (
          <Link to="/register" data-testid="founder-soldout-cta"
            className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold rounded-full px-6 py-3 text-sm">
            Sold out — join waitlist
          </Link>
        ) : (
          <Link to="/register?plan=founder" data-testid="founder-cta"
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full px-6 py-3 text-sm tactile-shadow whitespace-nowrap">
            Claim Founder seat
          </Link>
        )}
      </div>
    </div>
  );
}
